import type { AllComponents } from "../interfaces/base";

/** Flat map of components by key (uuid), same shape as builder `structure.components`; local type keeps `_default` free of store imports. */
type StructureComponentsByKey = Record<string, AllComponents>;

/** Builder sentinel editor id — must exist in structure for global Editor UI */
export const EDITOR_SENTINEL_ID = -11;

export function isEditorSentinelComponent(component: unknown): boolean {
  if (!component || typeof component !== "object") return false;
  const c = component as { id?: number; type?: string };
  return c.id === EDITOR_SENTINEL_ID || c.type === "editor";
}

export function isEditorSentinelRelation(relation: unknown): boolean {
  if (!relation || typeof relation !== "object") return false;
  return (relation as { child_id?: number }).child_id === EDITOR_SENTINEL_ID;
}

export function toTemplateChildren(source: unknown): AllComponents[] {
  if (Array.isArray((source as AllComponents)?.childElements)) {
    return (source as AllComponents).childElements as AllComponents[];
  }
  if (Array.isArray((source as { child_elements?: unknown })?.child_elements)) {
    return (source as { child_elements: AllComponents[] }).child_elements;
  }
  return [];
}

export function cloneComponentForSave(
  node: AllComponents,
  componentsMap: StructureComponentsByKey,
  visited: Set<string>
): AllComponents {
  const nodeUuid = node.uuid?.trim();
  const nodeKey = nodeUuid || String(node.id ?? "");
  if (!nodeKey) {
    return { ...node, childElements: [] } as AllComponents;
  }
  if (visited.has(nodeKey)) {
    return { ...node, childElements: [] } as AllComponents;
  }
  visited.add(nodeKey);

  const relations = (Array.isArray(node.childWebpageComponentRelations)
    ? node.childWebpageComponentRelations
    : []
  ).filter((relation) => !isEditorSentinelRelation(relation));
  const childElements = relations
    .map((relation) => {
      const childUuid = (relation as { child_uuid?: string | null }).child_uuid?.trim();
      if (!childUuid) {
        return null;
      }
      const child = componentsMap[childUuid];
      if (!child || isEditorSentinelComponent(child)) {
        return null;
      }
      return cloneComponentForSave(child as AllComponents, componentsMap, visited);
    })
    .filter((x): x is AllComponents => !!x);

  const keptChildUuids = new Set(
    childElements
      .map((child) => child.uuid?.trim())
      .filter((uuid): uuid is string => !!uuid)
  );
  const nextRelations = relations.filter((relation) => {
    const childUuid = (relation as { child_uuid?: string | null }).child_uuid?.trim();
    return !!childUuid && keptChildUuids.has(childUuid);
  });

  return {
    ...node,
    childElements,
    childWebpageComponentRelations: nextRelations,
  } as AllComponents;
}

export function buildComponentsMapForSave(
  root: AllComponents
): Record<string, AllComponents> {
  const map: Record<string, AllComponents> = {};
  const stack: AllComponents[] = [root];
  while (stack.length > 0) {
    const current = stack.pop()!;
    if (isEditorSentinelComponent(current)) {
      continue;
    }
    const uuid = current.uuid?.trim();
    if (!uuid) {
      continue;
    }
    map[uuid] = current;
    for (const child of toTemplateChildren(current)) {
      stack.push(child as AllComponents);
    }
  }
  return map;
}
